import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Loader2, Upload } from 'lucide-react';

export type ContentState = 'generating' | 'draft' | 'published' | 'modified';

interface ContentVersionBadgeProps {
  state: ContentState;
  hasUnpublishedChanges?: boolean;
  onPublish?: () => void;
  publishing?: boolean;
  className?: string;
}

export function ContentVersionBadge({ 
  state, 
  hasUnpublishedChanges,
  onPublish,
  publishing,
  className 
}: ContentVersionBadgeProps) {
  const getStateDisplay = () => {
    if (state === 'generating') {
      return {
        variant: 'secondary' as const,
        icon: <Loader2 className="h-3 w-3 mr-1 animate-spin" />,
        label: 'Generating...',
      };
    }

    if (state === 'published' && !hasUnpublishedChanges) {
      return {
        variant: 'default' as const,
        icon: <CheckCircle className="h-3 w-3 mr-1" />,
        label: 'Published',
      };
    }

    if (hasUnpublishedChanges) {
      return {
        variant: 'outline' as const,
        icon: <AlertCircle className="h-3 w-3 mr-1" />,
        label: 'Unpublished Changes',
      };
    }

    return {
      variant: 'secondary' as const,
      icon: null,
      label: 'Draft',
    };
  };

  const display = getStateDisplay();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Badge variant={display.variant} className="flex items-center">
        {display.icon}
        {display.label}
      </Badge>

      {hasUnpublishedChanges && onPublish && (
        <Button
          variant="default"
          size="sm"
          onClick={onPublish}
          disabled={publishing}
        >
          {publishing ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Upload className="h-3 w-3 mr-1" />
          )}
          Publish
        </Button>
      )}
    </div>
  );
}
